// src/components/MapPicker.js
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// IMPORTANT : Configuration des icônes
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Les 8 régions du Niger
const NIGER_REGIONS = [
  { name: "Agadez", capital: "Agadez", lat: 16.9742, lng: 7.9908 },
  { name: "Diffa", capital: "Diffa", lat: 13.3154, lng: 12.6110 },
  { name: "Dosso", capital: "Dosso", lat: 13.0490, lng: 3.1937 },
  { name: "Maradi", capital: "Maradi", lat: 13.4910, lng: 7.0964 },
  { name: "Niamey", capital: "Niamey", lat: 13.5137, lng: 2.1098 },
  { name: "Tahoua", capital: "Tahoua", lat: 14.8900, lng: 5.2670 },
  { name: "Tillabéri", capital: "Tillabéri", lat: 14.2024, lng: 1.4542 },
  { name: "Zinder", capital: "Zinder", lat: 13.8053, lng: 8.9883 }
];

const MapPicker = ({ 
  onLocationSelect, 
  initialPosition = [13.5137, 2.1098],
  initialZoom = 7,
  latitude = '',
  longitude = '',
  showSearchBar = true,
  showNigerRegions = true,
  isViewOnly = false,
  selectedSiteName = "",
  selectedSiteAddress = "",
  showInstructions = true
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [address, setAddress] = useState('');
  const [isMapReady, setIsMapReady] = useState(false);
  const prevLatLng = useRef({ lat: latitude, lng: longitude });

  // ✅ Fonction pour obtenir l'adresse (retourne l'adresse)
  const fetchAddress = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&accept-language=fr`
      );
      const data = await response.json();
      if (data.display_name) {
        setAddress(data.display_name);
        if (marker.current) {
          marker.current.setPopupContent(`<div style="text-align:center">
            <strong>${data.display_name}</strong><br>
            <small>Lat: ${lat.toFixed(6)} | Lng: ${lng.toFixed(6)}</small>
          </div>`);
        }
        return data.display_name;
      }
    } catch (error) {
      console.log('Erreur géocodage:', error);
    }
    return null;
  };

  // ✅ Initialisation de la carte (une seule fois)
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // CRÉATION DE LA CARTE SANS ANIMATIONS
    map.current = L.map(mapContainer.current, {
      center: initialPosition,
      zoom: initialZoom,
      dragging: !isViewOnly,
      touchZoom: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      boxZoom: !isViewOnly,
      keyboard: true,
      zoomControl: true,
      tap: true,
      fadeAnimation: false,
      zoomAnimation: false,
      markerZoomAnimation: false,
      attributionControl: false
    });

    // Ajouter les tuiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      minZoom: 3
    }).addTo(map.current);

    // Créer le marqueur initial
    const initialLat = parseFloat(latitude) || initialPosition[0];
    const initialLng = parseFloat(longitude) || initialPosition[1];
    
    if (!isNaN(initialLat) && !isNaN(initialLng)) {
      marker.current = L.marker([initialLat, initialLng], { 
        draggable: !isViewOnly 
      }).addTo(map.current);
      
      map.current.setView([initialLat, initialLng], initialZoom);
      
      if (isViewOnly) {
        const popupContent = `
          <div style="text-align:center; max-width:250px;">
            <strong>${selectedSiteName || 'Position'}</strong>
            ${selectedSiteAddress ? `<br><small>${selectedSiteAddress}</small>` : ''}
            <br><small>Lat: ${initialLat.toFixed(6)} | Lng: ${initialLng.toFixed(6)}</small>
          </div>
        `;
        marker.current.bindPopup(popupContent).openPopup();
      } else {
        marker.current.bindPopup('Position').openPopup();
      }
    }

    // ✅ Gestion du clic sur la carte - AVEC GÉOCODAGE
    if (!isViewOnly) {
      map.current.on('click', async (e) => {
        const { lat, lng } = e.latlng;
        
        if (marker.current) {
          marker.current.setLatLng([lat, lng]);
        }
        
        // Récupérer l'adresse
        const fetchedAddress = await fetchAddress(lat, lng);
        
        if (onLocationSelect) {
          onLocationSelect({
            latitude: lat,
            longitude: lng,
            address: fetchedAddress || address
          });
        }
      });
    }

    // ✅ Événement de déplacement du marqueur - AVEC GÉOCODAGE
    if (marker.current && !isViewOnly) {
      marker.current.on('dragend', async function() {
        const newPos = this.getLatLng();
        
        // Récupérer l'adresse
        const fetchedAddress = await fetchAddress(newPos.lat, newPos.lng);
        
        if (onLocationSelect) {
          onLocationSelect({
            latitude: newPos.lat,
            longitude: newPos.lng,
            address: fetchedAddress || address
          });
        }
      });
    }

    // ✅ Marquer la carte comme prête
    setTimeout(() => {
      if (map.current) {
        map.current.invalidateSize();
        setIsMapReady(true);
      }
    }, 300);

    // Nettoyage
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []); // S'exécute une seule fois

  // ✅ Mettre à jour le marqueur quand les coordonnées changent
  useEffect(() => {
    if (!map.current || !isMapReady) return;
    
    if (!latitude || !longitude) return;

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    
    if (isNaN(lat) || isNaN(lng)) return;

    // Éviter les mises à jour inutiles
    if (prevLatLng.current.lat === latitude && prevLatLng.current.lng === longitude) {
      return;
    }

    prevLatLng.current = { lat: latitude, lng: longitude };

    // Mettre à jour le marqueur
    if (marker.current) {
      marker.current.setLatLng([lat, lng]);
      
      if (isViewOnly) {
        const popupContent = `
          <div style="text-align:center; max-width:250px;">
            <strong>${selectedSiteName || 'Position'}</strong>
            ${selectedSiteAddress ? `<br><small>${selectedSiteAddress}</small>` : ''}
            <br><small>Lat: ${lat.toFixed(6)} | Lng: ${lng.toFixed(6)}</small>
          </div>
        `;
        marker.current.setPopupContent(popupContent);
      } else {
        marker.current.setPopupContent(`<div style="text-align:center">
          <strong>Position</strong><br>
          Lat: ${lat.toFixed(6)}<br>
          Lng: ${lng.toFixed(6)}
        </div>`);
      }
    }

    // Centrer la carte
    if (map.current) {
      map.current.setView([lat, lng], map.current.getZoom());
    }
    
  }, [latitude, longitude, isMapReady, isViewOnly, selectedSiteName, selectedSiteAddress]);

  // ✅ Recherche d'adresse - AVEC GÉOCODAGE
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || !map.current || isViewOnly) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1&accept-language=fr`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0];
        const position = [parseFloat(lat), parseFloat(lon)];
        
        if (marker.current) {
          marker.current.setLatLng(position);
        } else {
          marker.current = L.marker(position, { 
            draggable: true 
          }).addTo(map.current);
        }
        
        marker.current.bindPopup(`<div style="text-align:center">
          <strong>${display_name}</strong>
        </div>`).openPopup();
        
        map.current.setView(position, 7);
        
        setAddress(display_name);
        
        if (onLocationSelect) {
          onLocationSelect({
            latitude: position[0],
            longitude: position[1],
            address: display_name
          });
        }
        
        setSearchQuery('');
      } else {
        alert(`Aucun résultat trouvé pour: "${searchQuery}"`);
      }
    } catch (error) {
      console.error('Erreur recherche:', error);
      alert('Erreur lors de la recherche. Vérifiez votre connexion internet.');
    }
  };

  // ✅ Centrer sur une région - AVEC GÉOCODAGE
  const centerOnRegion = (region) => {
    if (!map.current || isViewOnly) return;
    
    const position = [region.lat, region.lng];
    const regionAddress = `${region.capital}, ${region.name}`;
    
    if (marker.current) {
      marker.current.setLatLng(position);
    } else {
      marker.current = L.marker(position, { 
        draggable: true 
      }).addTo(map.current);
    }
    
    marker.current.bindPopup(`<div style="text-align:center">
      <strong>${region.capital}</strong><br>
      <small>${region.name}</small>
    </div>`).openPopup();
    
    map.current.setView(position, 7);
    
    setAddress(regionAddress);
    
    if (onLocationSelect) {
      onLocationSelect({
        latitude: region.lat,
        longitude: region.lng,
        address: regionAddress
      });
    }
  };

  return (
    <div className="map-picker">
      {/* Barre de recherche */}
      {showSearchBar && !isViewOnly && (
        <div className="mb-3">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Rechercher n'importe où dans le monde..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              disabled={!isMapReady || isViewOnly}
            />
            <button 
              className="btn btn-primary" 
              onClick={handleSearch}
              disabled={!isMapReady || !searchQuery.trim() || isViewOnly}
            >
              <i className="bi bi-search"></i>
            </button>
          </div>
          <small className="text-muted mt-1 d-block">
            Exemples: "Mali", "Paris France", "New York", "Tokyo Japon"
          </small>
        </div>
      )}

      {/* Régions du Niger */}
      {showNigerRegions && !isViewOnly && (
        <div className="mb-3">
          <small className="text-muted d-block mb-2">
            <i className="bi bi-geo-alt me-1"></i>
            Les 8 régions du Niger :
          </small>
          <div className="d-flex flex-wrap gap-1">
            {NIGER_REGIONS.map((region) => (
              <button
                key={region.name}
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => centerOnRegion(region)}
                disabled={!isMapReady || isViewOnly}
                title={`${region.capital} - ${region.name}`}
              >
                {region.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Conteneur de la carte */}
      <div 
        ref={mapContainer}
        className="map-container"
        style={{ 
          height: isViewOnly ? '500px' : '400px',
          width: '100%',
          borderRadius: '8px',
          border: '2px solid #0B3C5D',
          backgroundColor: '#f8f9fa',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }}
      />

      {/* Instructions */}
      {showInstructions && !isViewOnly && (
        <div className="mt-3">
          <div className="row text-center small">
            <div className="col-md-4">
              <div className="text-muted">
                <i className="bi bi-arrows-move d-block fs-5 mb-1"></i>
                <div>Glissez la carte</div>
                <small>Maintenez clic + déplacez</small>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-muted">
                <i className="bi bi-mouse d-block fs-5 mb-1"></i>
                <div>Cliquez pour placer</div>
                <small>Un clic positionne et charge l'adresse</small>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-muted">
                <i className="bi bi-zoom-in d-block fs-5 mb-1"></i>
                <div>Zoomer</div>
                <small>Molette ou boutons +/-</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message mode consultation */}
      {isViewOnly && (
        <div className="alert alert-info mt-3">
          <div className="d-flex align-items-center">
            <i className="bi bi-info-circle me-2"></i>
            <div>
              <strong>Mode consultation</strong>
              <div className="small mt-1">
                Vous pouvez visualiser la position mais pas la modifier.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Adresse sélectionnée */}
      {address && !isViewOnly && (
        <div className="alert alert-success mt-3">
          <div className="d-flex align-items-center">
            <i className="bi bi-geo-alt-fill me-2"></i>
            <div>
              <strong>Position :</strong>
              <div className="small mt-1">{address}</div>
            </div>
          </div>
        </div>
      )}

      {/* CSS */}
      <style jsx="true">{`
        .map-container {
          cursor: ${isViewOnly ? 'default' : 'grab'} !important;
          touch-action: pan-x pan-y !important;
        }
        
        .map-container:active:not(.view-only) {
          cursor: grabbing !important;
        }
        
        .leaflet-container {
          cursor: inherit !important;
          font-family: inherit !important;
        }
        
        .leaflet-control-attribution {
          display: none !important;
        }
        
        .leaflet-control-zoom {
          border: 1px solid #ddd !important;
          border-radius: 4px !important;
          box-shadow: 0 1px 5px rgba(0,0,0,0.1) !important;
        }
        
        .leaflet-control-zoom a {
          background-color: white !important;
          color: #333 !important;
          border-bottom: 1px solid #eee !important;
          width: 32px !important;
          height: 32px !important;
          line-height: 32px !important;
          font-size: 18px !important;
        }
        
        .leaflet-control-zoom a:hover {
          background-color: #f8f9fa !important;
          color: #0B3C5D !important;
        }
        
        .leaflet-popup-content {
          font-size: 14px;
          line-height: 1.4;
          padding: 8px;
        }
        
        .leaflet-popup-content-wrapper {
          border-radius: 8px;
          box-shadow: 0 3px 10px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
};

export default MapPicker;